import dateFormat from 'dateformat';
import {parse, toSeconds} from 'iso8601-duration';
import stats from 'stats-lite';

const ONE_SECOND_MS = 1000;
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;

const GRAPH_FORMAT = 'ddd dd/mm hh:MM TT';
const JUST_DATE_FORMAT = 'yyyy-mm-dd';
export const GRAPH_FORMAT_MOMENT = 'ddd DD/MM hh:mm A';

const _findDisplayTimeIndexFromAvailableTimes = (selected_time, available_times = {}) => {
  let available_time_index;
  if (available_times === {} || available_times.contained_points === {}) {
    available_time_index = undefined;
  } else {
    available_time_index = available_times.contained_points[selected_time];
  }
  return available_time_index;
};

export const findDisplayTimeFromAvailableTimes = (selected_time, available_times = {}) => {
  let display_time = undefined;
  let available_time_index = _findDisplayTimeIndexFromAvailableTimes(selected_time, available_times);
  if (available_time_index !== undefined) {
    display_time = available_times.intervals[available_time_index].point_at;
  }
  return display_time;
};

export const getMostFrequentDuration = (time_points) => {
  // takes array of iso8601 dates
  // returns one iso8601 duration
  let durations = time_points.map((value, index) => {
    const duration_ms = Date.parse(time_points[index + 1]) - Date.parse(value);
    return (duration_ms / ONE_MINUTE_MS);
  });
  return ('PT' + stats.mode(durations) + 'M');
};

export const getIntervals = (time_points, duration, valid_times) => {
  const duration_ms = toSeconds(parse(duration)) * ONE_SECOND_MS;
  const all_valid_periods = {};
  const valid_points = [];

  let interval_objects = time_points.map((value, index) => {
    const start_time = new Date(value);
    const start_time_ms = start_time.getTime();
    const start_time_plus_most_frequent_duration_ms = start_time_ms + (duration_ms);
    let end_time_ms;
    const next_time = time_points[(index + 1)];

    if (next_time) {
      let next_time_timestamp = new Date(time_points[(index + 1)]).getTime();
      if (next_time_timestamp < start_time_plus_most_frequent_duration_ms) {
        end_time_ms = next_time_timestamp;
      } else {
        end_time_ms = start_time_plus_most_frequent_duration_ms;
      }
    } else {
      end_time_ms = start_time_plus_most_frequent_duration_ms;
    }

    let end_time_less_one_minute_ms = end_time_ms - ONE_MINUTE_MS;

    let contained_periods = [];
    valid_times.forEach((valid_time) => {
      let valid_time_ms = new Date(valid_time).getTime();
      if (valid_time_ms === start_time_ms) {
        valid_points.push(valid_time);
      }
      if (valid_time_ms >= start_time_ms && valid_time_ms <= end_time_less_one_minute_ms) {
        contained_periods.push(valid_time);
        all_valid_periods[valid_time] = index;
      }
    });

    return ({
      'point_at': start_time.toISOString(),
      'starting_at': start_time.toISOString(),
      'ending_at': new Date(end_time_less_one_minute_ms).toISOString(),
      'contained_periods': contained_periods
    });
  });

  return ({
    'interval_objects': interval_objects,
    'all_valid_periods': all_valid_periods,
    'valid_points': valid_points
  });
};

// Returns the interval that contains the time.
// Intervals param should be an array of intervals as created by the getIntervals function above.
export const findInterval = (intervals, time) => {
  const time_ms = new Date(time).getTime();
  let len = intervals.length;
  for (let i = 0; i < len; i++) {
    const start_ms = new Date(intervals[i].starting_at).getTime();
    if (time_ms >= start_ms) {
      const end_ms = new Date(intervals[i].ending_at).getTime();
      if (time_ms <= end_ms) {
        return intervals[i];
      }
    }
  }
  return null;
};

export const addDays = (time, days) => {
  if (!time) {
    return time;
  }
  const parsed_time = new Date(time);
  const currentDate = parsed_time.getDate();
  parsed_time.setDate(currentDate + days);
  return (parsed_time.toISOString());
};

// TODO: Handle daylight saving time ********************************************************
export const utcToLocal = (time) => {
  if (!time) {
    return time;
  }
  return (new Date(utcToLocalTime(time)).toISOString());
};

export const utcToLocalTime = (time) => {
  if (!time) {
    return time;
  }
  const parsed_time = new Date(time);
  const mins_offset = parsed_time.getTimezoneOffset();
  const current_time = parsed_time.getTime();
  return current_time - mins_offset * ONE_MINUTE_MS;
};

export const flooredHour = (time) => {
  let parsed_time = new Date(time);
  parsed_time.setMinutes(0);
  parsed_time.setSeconds(0);
  parsed_time.setMilliseconds(0);
  return (parsed_time.toISOString());
};

export const roundedHour = (time) => {
  let parsed_time = new Date(time);
  const mins = parsed_time.getMinutes();
  if (mins < 30) {
    parsed_time.setMinutes(0);
  } else {
    parsed_time.setMinutes(0);
    parsed_time.setHours(parsed_time.getHours() + 1);
  }

  parsed_time.setSeconds(0);
  parsed_time.setMilliseconds(0);
  return (parsed_time.toISOString());
};

export const lastMidnightStr = (time) => {
  if (!time) {
    return time;
  }
  return lastMidnightDate(time).toISOString();
};

export const lastMidnightDate = (time) => {
  let parsed_time = new Date(time);
  parsed_time.setHours(0);
  parsed_time.setMinutes(0);
  parsed_time.setSeconds(0);
  parsed_time.setMilliseconds(0);
  return parsed_time;
};

export const formatForGraph = (time) => {
  return dateFormat(time, GRAPH_FORMAT);
};

export const formatAsDate = (time) => {
  return dateFormat(time, JUST_DATE_FORMAT);
};

export const formatTime = (time) => {
  return dateFormat(time, 'HH:MM');
};

export const formatDefault = (date) => {
  return dateFormat(date, 'dd mmm yyyy HH:MM');
};

export const isTimeOver12hrs = (time) => {
  if (!time) {
    return false;
  }
  const now = new Date();
  const givenTime = new Date(time);
  const timeDifference = now.getTime() - givenTime.getTime();
  return Math.abs(timeDifference / ONE_HOUR_MS) > 12;
};



// WEBPACK FOOTER //
// ./src/utils/time_utils.js